/**
 * useAttention Hook - Fetches attention items across all streams
 *
 * WS-aware:
 * - Instant removal when node_status_changed away from 'attention'
 * - Fetch-on-add when node_status_changed to 'attention' (NodeStatusChangedEvent
 *   lacks stream_name/meta/name, so we refetch rather than synthesizing the item)
 * - 2 min HTTP reconciliation when WS connected (covers unsubscribed streams)
 * - Falls back to 60s HTTP polling when WS disconnected
 */

import { axiom } from '../lib/axiom';
import { useState, useCallback, useEffect, useRef } from 'react';
import { api, ApiError } from '../lib/api';
import { useUserWebSocket } from '../contexts/UserWebSocketContext';
import type { AttentionNode } from '../types/api';
import type { StreamEvent } from '@pistachiorama/shared-types';

const POLL_INTERVAL_FALLBACK = 60 * 1000;       // 60s when WS disconnected
const RECONCILE_INTERVAL_WS = 2 * 60 * 1000;   // 2 min when WS connected

// AttentionItem format expected by AttentionPanel
export interface AttentionItem {
  id: string;
  stream: string;
  streamId: string;
  task: string;
  nodeId: string;
  reason: string;
  reasonType: 'decision' | 'error' | 'input';
  timestamp: string;
}

interface UseAttentionReturn {
  items: AttentionItem[];
  count: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Format relative time from date string
 */
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

/**
 * Extract reason type from node meta or default to 'input'
 */
function extractReasonType(meta: string | null): 'decision' | 'error' | 'input' {
  if (!meta) return 'input';

  try {
    const parsed = JSON.parse(meta);
    if (parsed.reasonType && typeof parsed.reasonType === 'string') {
      const type = parsed.reasonType.toLowerCase();
      if (type === 'decision' || type === 'error' || type === 'input') {
        return type;
      }
    }
    // Check for error indicators in description
    if (parsed.description?.toLowerCase().includes('error') ||
        parsed.description?.toLowerCase().includes('fail')) {
      return 'error';
    }
    if (parsed.description?.toLowerCase().includes('decision') ||
        parsed.description?.toLowerCase().includes('choose')) {
      return 'decision';
    }
  } catch {
    // Not valid JSON, check raw string
    const lower = meta.toLowerCase();
    if (lower.includes('error') || lower.includes('fail')) return 'error';
    if (lower.includes('decision') || lower.includes('choose')) return 'decision';
  }

  return 'input';
}

/**
 * Extract reason text from node meta or use task name
 */
function extractReason(meta: string | null, taskName: string): string {
  if (!meta) return `Needs attention: ${taskName}`;

  try {
    const parsed = JSON.parse(meta);
    if (parsed.reason) return parsed.reason;
    if (parsed.description) return parsed.description;
  } catch {
    // Not valid JSON
  }

  return `Needs attention: ${taskName}`;
}

/**
 * Transform API response to AttentionItem format
 */
function transformAttentionNode(node: AttentionNode): AttentionItem {
  return {
    id: node.id,
    stream: node.stream_name,
    streamId: node.stream_id,
    task: node.name,
    nodeId: node.id,
    reason: extractReason(node.meta, node.name),
    reasonType: extractReasonType(node.meta),
    timestamp: formatRelativeTime(node.updated_at),
  };
}

export function useAttention({ pollInterval }: { pollInterval?: number } = {}): UseAttentionReturn {
  const [items, setItems] = useState<AttentionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { userWebSocket, status } = useUserWebSocket();
  const wsConnected = status === 'connected';
  // null = "not yet observed" (prevents spurious fetch on initial mount when already connected)
  const prevConnectedRef = useRef<boolean | null>(null);

  const refresh = useCallback(async () => {
    try {
      const response = await api.attention.list();
      const transformed = response.map(transformAttentionNode);
      setItems(transformed);
      setError(null);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load attention items';
      setError(message);
      axiom.error('Failed to load attention items', { error: err instanceof Error ? err.message : String(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    refresh();
  }, [refresh]);

  // WS reconnect: fetch immediately to sync (only on disconnect→connect transition, not initial mount)
  useEffect(() => {
    const prev = prevConnectedRef.current;
    prevConnectedRef.current = wsConnected;
    if (wsConnected && prev === false) {
      refresh();
    }
  }, [wsConnected, refresh]);

  // Polling interval: 60s fallback when WS down, 2 min reconciliation when WS up
  useEffect(() => {
    const interval = setInterval(refresh, pollInterval ?? (wsConnected ? RECONCILE_INTERVAL_WS : POLL_INTERVAL_FALLBACK));
    return () => clearInterval(interval);
  }, [refresh, wsConnected, pollInterval]);

  // WS event listener for instant attention list updates
  useEffect(() => {
    if (!userWebSocket) return;

    // Debounce fetch-on-add: coalesce rapid-fire attention events into one HTTP call
    let fetchTimer: ReturnType<typeof setTimeout> | null = null;
    const debouncedFetch = () => {
      if (fetchTimer) clearTimeout(fetchTimer);
      fetchTimer = setTimeout(() => {
        fetchTimer = null;
        refresh();
      }, 300);
    };

    const handler = (_streamId: string, event: StreamEvent) => {
      if (event.type !== 'node_status_changed') return;

      if (event.status === 'attention') {
        // Can't synthesize a full AttentionItem from this event (no stream_name/meta/name).
        // Known limitation: if a removal event arrives while this fetch is in-flight,
        // the fetch response may re-add the removed item. Self-corrects at next 2-min reconciliation.
        // Debounced fetch to coalesce burst of attention events into one HTTP call.
        debouncedFetch();
      } else {
        // Instant removal — no HTTP needed
        setItems(prev => prev.filter(item => item.nodeId !== event.node_id));
      }
    };

    userWebSocket.on('streamEvent', handler);
    return () => {
      userWebSocket.off('streamEvent', handler);
      if (fetchTimer) clearTimeout(fetchTimer);
    };
  }, [userWebSocket, refresh]);

  return {
    items,
    count: items.length,
    loading,
    error,
    refresh,
  };
}
